import { useState, useCallback, useEffect } from "react";
import { DebitNoteCountsEntity, GetDebitNoteCountsUseCase } from "../../domain";
import { DebitNoteRepositoryImpl } from "../../data/repositories/DebitNoteRepositoryImpl";

// Create repository instance (singleton)
const debitNoteRepository = new DebitNoteRepositoryImpl();

// Create use case
const getDebitNoteCountsUseCase = new GetDebitNoteCountsUseCase(
  debitNoteRepository
);

export interface UseDebitNoteCountsReturn {
  counts: DebitNoteCountsEntity | null;
  loading: boolean;
  error: string | null;
  fetchCounts: (params?: {
    date_from?: string;
    date_to?: string;
  }) => Promise<void>;
  clearError: () => void;
}

export const useDebitNoteCounts = (initialParams?: {
  date_from?: string;
  date_to?: string;
}): UseDebitNoteCountsReturn => {
  const [counts, setCounts] = useState<DebitNoteCountsEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dateFrom = initialParams?.date_from;
  const dateTo = initialParams?.date_to;

  /**
   * Fetch debit note counts
   */
  const fetchCounts = useCallback(async (params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result = await getDebitNoteCountsUseCase.execute(params);
      setCounts(result);
    } catch (error: any) {
      const errorMessage = error.message || "Failed to load debit note counts";
      setError(errorMessage);
      console.error("Failed to fetch debit note counts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Load initial data on mount
  useEffect(() => {
    fetchCounts({ date_from: dateFrom, date_to: dateTo });
  }, [fetchCounts, dateFrom, dateTo]);

  return {
    counts,
    loading,
    error,
    fetchCounts,
    clearError,
  };
};
